import {DatePipe} from '@angular/common';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, signal} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Lottery, TixxEvent, TixxEventType} from '../../models';
import {LotteryStatusTranslationService} from 'src/app/shared/translations';
import {ExecutionIteration, EventKpis} from '../../models';
import {environment} from '~environments/environment';
import {ExecutionIterationStatusType} from '../../enum';

/**
 * Service for creating and updating tixx events.
 */
@Injectable({
  providedIn: 'root'
})
export class TixxSelectService {
  public API_URL: any = `${environment.envAPI}`;
  public API_URL_MEDIA: any = `${environment.envAPI}/l/media`;

  public lotteryList: BehaviorSubject<Array<Lottery>> = new BehaviorSubject<Array<Lottery>>(null);
  public castLotteryList: any = this.lotteryList.asObservable();

  public selectedLottery: BehaviorSubject<Lottery> = new BehaviorSubject<Lottery>(null);
  public castSelectedLottery: any = this.selectedLottery.asObservable();

  public savedFilters = signal(null);

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
    private t: LotteryStatusTranslationService,
  ) {
  }

  /**
   * Get all the available tixx events.
   */
  public getTixxEvents(id: number): Observable<Array<TixxEvent>> {
    return this.http.get<Array<TixxEvent>>(`${this.API_URL}/l/tixx/organizers/${id}/events`).pipe(
      map((response: Array<TixxEvent>) => response['hydra:member'].map(tixxEvent => new TixxEvent(tixxEvent, this.datePipe)))
    );
  }

  /**
   * Get all the available tixx event types.
   */
  public getTixxEventTypes(id: number): Observable<Array<TixxEventType>> {
    return this.http.get<Array<TixxEventType>>(`${this.API_URL}/l/organizers/${id}/lottery-event-types`).pipe(
      map((response: Array<TixxEventType>) => response['hydra:member'].map(tixxEventType => new TixxEventType(tixxEventType)))
    );
  }

  /**
   * Get all the lottery events.
   */
  public getLotteries(id: number): Observable<Array<Lottery>> {
    return this.http.get<Array<Lottery>>(`${this.API_URL}/l/organizers/${id}/lotteries`).pipe(
      map((response: Array<Lottery>) => response['hydra:member'].map(lotteryEvent => new Lottery(lotteryEvent, this.t)))
    );
  }

  /**
   * Get lottery by id.
   */
  public getLottery(id: number): Observable<Lottery> {
    return this.http.get<Lottery>(`${this.API_URL}/l/lotteries/${id}`).pipe(
      map((response: Lottery) => new Lottery(response, this.t))
    );
  }

  /**
   * Get event KPIs by id.
   */
  public getEventKpis(id: number): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/l/lotteries/${id}/kpis/event`).pipe(
      map((response: any) => new EventKpis(response))
    );
  }

  /**
   * Get execution iterations by id.
   */
  public getExecutionIterations(id: number, params: HttpParams): Observable<Array<ExecutionIteration>> {
    return this.http.get<Array<ExecutionIteration>>(`${this.API_URL}/l/executions/${id}/iterations`, {params}).pipe(
      map((response: Array<ExecutionIteration>) => response['hydra:member'].map(executionIteration => new ExecutionIteration(executionIteration, this.t)))
    );
  }

  /**
   * Get execution iterations by id.
   */
  public getExecutionIterationEndpoint(id: number): any {
    const api = `${this.API_URL}/l/executions/${id}/iterations`;

    const params: Array<any> = [
      {'status[]': [ExecutionIterationStatusType.COMPLETED, ExecutionIterationStatusType.COMPLETED_WITH_ERRORS]},
    ];

    return [api, params];
  }

  /**
   * Create execution iteration object.
   */
  public createExecutionIterationObject(iterations: Array<ExecutionIteration>): Array<ExecutionIteration> {
    return iterations.map((iterationData: ExecutionIteration) => new ExecutionIteration(iterationData, this.t));
  }

  /**
   * Create a new lottery event.
   */
  public postLottery(tixxEvent: any): Observable<Lottery> {
    return this.http.post<Lottery>(`${this.API_URL}/l/lotteries`, tixxEvent).pipe(
      map((lottery: Lottery) => new Lottery(lottery, this.t))
    );
  }

  /**
   * Update a specific lottery event.
   */
  public updateLottery(id: number, tixxEvent: any): Observable<Lottery> {
    return this.http.patch<Lottery>(`${this.API_URL}/l/lotteries/${id}`, tixxEvent).pipe(
      map((lottery: Lottery) => new Lottery(lottery, this.t))
    );
  }

  /**
   * Update a specific lottery event.
   */
  public updateLotteryStatus(id: number, status: any): Observable<Lottery> {
    return this.http.patch<Lottery>(`${this.API_URL}/l/lotteries/${id}/status`, status).pipe(
      map((lottery: Lottery) => new Lottery(lottery, this.t))
    );
  }

  public getSubmittedTickets(id: any): Observable<any> {
    return this.http.get(`${this.API_URL}/l/lotteries/${id}/export/csv`, {responseType: 'arraybuffer'});
  }

  /**
   * Create a hero image.
   */
  public postHeroImage(file: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL_MEDIA}/hero-image`, file);
  }

  /**
   * Get the api endpoint for the lotteries.
   */
  public getLotteriesEndpoint(id: number): any {
    const api = `${this.API_URL}/l/organizers/${id}/lotteries`;

    const params: Array<any> = [];

    return [api, params];
  }

  /**
   * Set a new selected lottery
   */
  public setSelectedLottery(lottery: Lottery): void {
    this.selectedLottery.next(lottery);
  }

  /**
   * Set a new lottery list
   */
  public setLotteryList(lotteryList: Array<Lottery>): void {
    this.lotteryList.next(lotteryList);
  }

}
